/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
import { Fragment, h } from 'preact';
import { useContext, useEffect, useRef, useState } from 'preact/hooks';
import { LABEL_ECO_QUALITY } from '../../../../../core/quality/types';
import { ECO_CLOSED, ECO_OPENED, USER_CLICK, USER_ECO_TOGGLED, USER_KEYBOARD, USER_QUALITY_CHANGED } from '../../../../../types';
import { usePlayerContext, useStyles } from '../../../../common/hooks';
import { connect, withBreakPoints } from '../../../hoc';
import { Button, ToggleButton } from '../../common';
import { Modal } from '../../common/modal';

import { PIANO_VALUE_POSITION_ECO } from '../../../../../monitoring/piano/types';
import { IconContext } from '../../../../common/components/svg/icons/icon-context';
import { useOpenDialogsCounter, useRegisterDialog } from '../../../../common/context/DialogContext';
import Scrollable from '../../../../common/hoc/scrollable';
import { TEXT_LEVEL_1_FONT_SIZE, TEXT_LEVEL_2_FONT_SIZE, TEXT_LEVEL_3_FONT_SIZE } from '../../../../common/styles';
import { isValidateKeyboard } from '../../../../common/utils/keyboard';
import { BUTTON_SQUARE_SIZE } from '../../../styles';
import { LABELS_FILL_COLOR, PRIMARY_FILL_COLOR, SEPARATOR_COLOR } from '../../../theme/colors';
import '../../../theme/styles/eco.css';
import { handleCloseModalButton } from '../../../utils/modal';
import { MODAL_BASIC_STYLE, MODAL_CLOSING_BUTTON_STYLE } from '../../common/modal/types';
import { ECO_STYLES_FOOTER, SETTINGS_ICON_SIZE } from './styles';
import { ECO_DEFAULT_TEXTS, ECO_MOBILE_TEXTS } from './types';

function EcoButton({
  waitStart,
  isAd,
  hasQualities,
  currentQualityLabel,
  selectedQualityLevel,
  ecoQuality,
  mobile,
  tablet,
  isFullscreen,
  width: magnetoWidth,
  height: magnetoHeight,
  small,
  extraSmall
}) {
  const Icon = useContext(IconContext);
  const [isOpened, setIsOpened] = useState(false);
  const [isOutsideModal, setIsOutsideModal] = useState(null);
  const [isFocusable, setIsFocusable] = useState(true);
  const [isEco, setIsEco] = useState(selectedQualityLevel === ecoQuality?.level);
  const [maxHeight, setMaxHeight] = useState('100%');
  const closeIconRef = useRef(null);
  const iconRef = useRef(null);
  const wrapperRef = useRef(null);
  const player = usePlayerContext();
  const setStyles = useStyles();

  const { title, title2, body1, body2, footer } = mobile || tablet ? ECO_MOBILE_TEXTS : ECO_DEFAULT_TEXTS;

  const setEcoMode = ({ action, source }) => {
    const { bitrate, resolution, level, label } = !isEco
      ? ecoQuality
      : { bitrate: 0, resolution: 0, level: -1, label: 'automatique' };

    player.setVideoQuality({ bitrate, resolution });
    player.userEvents$.next({ action, source, value: !isEco });
    player.userEvents$.next({
      action: USER_QUALITY_CHANGED,
      source,
      value: {
        position: PIANO_VALUE_POSITION_ECO,
        previousLabel: currentQualityLabel,
        newLabel: label,
        level,
        bitrate,
        resolution
      }
    });
    setIsEco(!isEco);
  };

  useRegisterDialog({
    id: 'eco',
    openingMode: 'click',
    state: [isOpened, setIsOpened]
  });
  useOpenDialogsCounter(isOpened);
  useEffect(() => setIsEco(selectedQualityLevel === ecoQuality?.level), [selectedQualityLevel, ecoQuality?.level]);

  const ecoModeActivated = currentQualityLabel === LABEL_ECO_QUALITY || isEco || selectedQualityLevel === ecoQuality?.level;
  const focusableItem = isFocusable ? 'ftv-magneto--focusable-item' : '';

  useEffect(() => {
    if (isOpened && closeIconRef.current) {
      setIsOutsideModal(false);
      closeIconRef.current.focus();
    }
  }, [isOpened, isOutsideModal]);

  // configure scrollarea for small breakpoint
  useEffect(
    // 104px is the size of the modal header we have to substract
    () => {
      if (magnetoWidth < magnetoHeight) setMaxHeight('max-content');
      else if (isOpened) setMaxHeight(`${magnetoHeight - 104}px`);
    },
    [isOpened, magnetoWidth]
  );

  const modalEcoIconStyle = {
    display: 'inline',
    ...setStyles(SETTINGS_ICON_SIZE)
  };

  const handleCloseModal = (event) => {
    handleCloseModalButton({
      event,
      player,
      currentIconRef: iconRef.current,
      setIsModalOpened: setIsOpened,
      closedAction: ECO_CLOSED
    });
  };

  return (hasQualities && (
    <Fragment>
      <Button
        hidden={isAd || waitStart}
        type="icon"
        openingMode="click"
        label="Mode éco"
        xs={small || extraSmall}
        ariaLabel="Mode éco"
        forceLabel
        zone="bottom"
        name="btn-eco"
        onClick={() => {
          player.userEvents$.next({
            action: ECO_OPENED,
            value: ecoModeActivated,
            source: USER_CLICK
          });
          setIsOpened(true);
        }}
        size={setStyles(BUTTON_SQUARE_SIZE).width}
        icon={ecoModeActivated ? 'eco-activated' : 'eco'}
        iconRef={iconRef}
        disableFocus={isOpened}
        disableCloseFocus
        ariaHidden={isOpened}
        ariaProps={{
          'aria-haspopup': 'dialog',
          'aria-controls': 'eco-modal',
          'aria-expanded': isOpened
        }}
      />

      {isOpened && (
        <Modal
          trap
          id="eco-modal"
          show={isOpened}
          attachedTo="btn-eco"
          styles={MODAL_BASIC_STYLE}
          onChange={(openedState) => {
            setIsOutsideModal(true);
            setIsOpened(openedState);
          }}
          onEscapeKeyDown={handleCloseModal}
          closingButtonStyle={setStyles(MODAL_CLOSING_BUTTON_STYLE)}
          closeComponent={(
            <Button
              type="icon"
              openingMode="click"
              label="Fermer"
              ariaLabel="Fermer la fenêtre Mode éco et retourner au lecteur vidéo"
              forceLabel
              disableFocus={false}
              last
              zone="bottom"
              onClick={handleCloseModal}
              size={setStyles(SETTINGS_ICON_SIZE).size}
              icon="close"
              xs
              iconRef={closeIconRef}
              name="btn-close-eco"
              ariaHidden={false}
            />
          )}
        >
          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
          <div
            ref={wrapperRef}
            className="ftv-magneto--eco-wrapper"
            style={setStyles({
              extraSmall: { padding: '20px 0 20px 20px', lineHeight: 'inherit' },
              default: { padding: '20px 0 20px 24px', color: LABELS_FILL_COLOR }
            })}
            onKeyDown={() => setIsFocusable(true)}
            onClick={() => setIsFocusable(false)}
          >
            {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
            <div
              className={`ftv-magneto--eco-header ${focusableItem}`}
              role="heading"
              aria-level="2"
              style={setStyles({ default: { gap: 10 }, extraSmall: { gap: 6 } })}
            >
              <Icon
                width={modalEcoIconStyle.size}
                height={modalEcoIconStyle.size}
                color={PRIMARY_FILL_COLOR}
                name={ecoModeActivated ? 'eco-activated' : 'eco'}
              />

              <div
                style={{
                  ...setStyles(TEXT_LEVEL_1_FONT_SIZE),
                  color: PRIMARY_FILL_COLOR
                }}
              >
                {title}
              </div>
            </div>

            {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
            <div
              className={`ftv-magneto--eco-status ${focusableItem}`}
              style={setStyles({
                // Marge A & B
                default: { marginTop: '20px', marginBottom: '20px' },
                small: { marginBottom: '16px' },
                extraSmall: { marginBottom: '14px' },
                'medium+large+extraLarge': { marginTop: '28px' }
              })}
              role="button"
              tabIndex="0"
              onKeyDown={(e) => {
                if (isValidateKeyboard(e)) {
                  setEcoMode({
                    action: USER_ECO_TOGGLED,
                    source: USER_KEYBOARD
                  });
                  e.preventDefault();
                  e.stopPropagation();
                }
              }}
            >
              <div
                className={`ftv-magneto--eco-status-label ${focusableItem}`}
                style={{
                  ...setStyles(TEXT_LEVEL_2_FONT_SIZE),
                  fontWeight: 700
                }}
                aria-live="assertive"
              >
                {title}
                {' '}
                (
                {ecoModeActivated ? 'activé' : 'désactivé'}
                )
              </div>
              <div>
                <div
                  className="ftv-magneto--eco-status-toggle"
                  style={setStyles({ extraSmall: { marginRight: '20px' } })}
                >
                  <ToggleButton
                    val={ecoModeActivated}
                    onToggle={() => {
                      setEcoMode({
                        action: USER_ECO_TOGGLED,
                        source: USER_CLICK
                      });
                    }}
                    ariaLabel={`${ecoModeActivated ? 'Désactiver' : 'Activer'} mode éco`}
                  />
                </div>
              </div>
            </div>

            {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
            <div className="ftv-magneto--eco-body">
              <Scrollable
                {...(!isFullscreen
                  ? setStyles({
                    default: { maxHeight: 350 },
                    medium: { maxHeight: 320 },
                    large: { maxHeight: 420 },
                    extraLarge: { maxHeight: 420 },
                    'extraSmall+small': { maxHeight }
                  })
                  : {})}
              >
                <div
                  style={setStyles({
                    default: { marginBottom: '20px', paddingRight: '24px' },
                    extraSmall: { paddingRight: '20px' }
                  })}
                >
                  <p
                    className={`ftv-magneto--eco-text-1 ${focusableItem}`}
                    style={{
                      ...setStyles(TEXT_LEVEL_2_FONT_SIZE),
                      marginTop: 0
                    }}
                  >
                    {body1}
                  </p>
                  <div
                    className={`ftv-magneto--separator-line ${focusableItem}`}
                    style={{ backgroundColor: SEPARATOR_COLOR }}
                  />

                  <div
                    className={`ftv-magneto--eco-text-2-title ${focusableItem}`}
                    role="heading"
                    aria-level="3"
                    style={{
                      ...setStyles(TEXT_LEVEL_2_FONT_SIZE),
                      fontWeight: 700
                    }}
                  >
                    {title2}
                  </div>

                  <p
                    className={`ftv-magneto--eco-text-2-body ${focusableItem}`}
                    style={{
                      ...setStyles(TEXT_LEVEL_2_FONT_SIZE),
                      marginTop: 0
                    }}
                  >
                    {body2}
                  </p>

                  <p
                    className={`ftv-magneto--eco-text-2-footer ${focusableItem}`}
                    style={{
                      ...setStyles(TEXT_LEVEL_3_FONT_SIZE),
                      ...ECO_STYLES_FOOTER
                    }}
                  >
                    {footer}
                  </p>
                </div>
              </Scrollable>
            </div>
          </div>
        </Modal>
      )}
    </Fragment>
  ));
}

const selector = ({
  playback: { hasQualities, currentQualityLabel, mappedQualities, selectedQualityLevel, ecoQuality },
  media: {
    isAd
  },
  ui: { waitStart, isFullscreen }
}) => ({
  isAd, waitStart, hasQualities, currentQualityLabel, mappedQualities, isFullscreen, selectedQualityLevel, ecoQuality
});

export default withBreakPoints(connect(selector)((EcoButton)));
